import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, deleteUser } from 'firebase/auth';
import { db, auth, getCompanyId } from '../firebase';
import { authFlows } from '../authFlows';
import Navbar from '../components/Navbar';
import { TIME_OFF_TYPES, getLimits } from '../timeOff';

export default function Settings() {
  const [savedDomain, setSavedDomain] = useState(null);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [limitInputs, setLimitInputs] = useState({ sick: '', vacation: '' });
  const [limitsSaving, setLimitsSaving] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState(null); // { ok, text }

  useEffect(() => {
    const load = async () => {
      const companyId = await getCompanyId();
      const snap = await getDoc(doc(db, 'settings', companyId));
      const data = snap.exists() ? snap.data() : null;
      if (data?.allowedDomain) {
        setSavedDomain(data.allowedDomain);
      }
      const limits = getLimits(data);
      setLimitInputs({
        sick: String(limits.sick),
        vacation: String(limits.vacation),
      });
      setLoading(false);
    };
    load();
  }, []);

  const handleSaveLimits = async (e) => {
    e.preventDefault();
    setLimitsMsg(null);
    const parsed = {};
    for (const key of Object.keys(TIME_OFF_TYPES)) {
      const n = parseInt(limitInputs[key], 10);
      if (isNaN(n) || n < 0 || n > 365) {
        setLimitsMsg({ ok: false, text: `${TIME_OFF_TYPES[key].label} allowance must be a number between 0 and 365.` });
        return;
      }
      parsed[key] = n;
    }
    setLimitsSaving(true);
    try {
      const companyId = await getCompanyId();
      await setDoc(doc(db, 'settings', companyId), { timeOffLimits: parsed }, { merge: true });
      setLimitsMsg({ ok: true, text: 'Time-off allowances saved.' });
    } catch (err) {
      setLimitsMsg({ ok: false, text: err.message });
    }
    setLimitsSaving(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = domain.trim().toLowerCase();
    if (!trimmed.startsWith('@') || trimmed.length < 2) {
      setError('Domain must start with @, e.g. @yourcompany.com');
      return;
    }
    setSaving(true);
    try {
      const companyId = await getCompanyId();
      await setDoc(doc(db, 'settings', companyId), { allowedDomain: trimmed }, { merge: true });
      setSavedDomain(trimmed);
      setDomain('');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError('');
    if (!deletePassword) {
      setDeleteError('Enter your password to confirm.');
      return;
    }
    if (!window.confirm(
      'Permanently delete your company account? This erases all employees, shifts and time entries. This cannot be undone.'
    )) return;

    setDeleting(true);
    try {
      const user = auth.currentUser;
      // Recent login is required for account deletion
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);

      authFlows.deleting = true;
      const companyId = await getCompanyId();

      const shiftsSnap = await getDocs(query(collection(db, 'shifts'), where('companyId', '==', companyId)));
      for (const d of shiftsSnap.docs) await deleteDoc(d.ref);

      const entriesSnap = await getDocs(query(collection(db, 'timeEntries'), where('companyId', '==', companyId)));
      for (const d of entriesSnap.docs) await deleteDoc(d.ref);

      const timeOffSnap = await getDocs(query(collection(db, 'timeOffRequests'), where('companyId', '==', companyId)));
      for (const d of timeOffSnap.docs) await deleteDoc(d.ref);

      // Employee user docs first — rules check the admin's own doc, so it goes last
      const usersSnap = await getDocs(query(collection(db, 'users'), where('companyId', '==', companyId)));
      for (const d of usersSnap.docs) {
        if (d.id !== user.uid) await deleteDoc(d.ref);
      }

      await deleteDoc(doc(db, 'settings', companyId));
      await deleteDoc(doc(db, 'companies', companyId));
      if (companyId !== user.uid) {
        // Legacy accounts: company/settings docs may be keyed by the admin uid
        await deleteDoc(doc(db, 'settings', user.uid));
        await deleteDoc(doc(db, 'companies', user.uid));
      }
      await deleteDoc(doc(db, 'users', user.uid));

      await deleteUser(user);
      alert('Your account has been permanently deleted.');
      // onAuthStateChanged in App.js routes back to the login screen
    } catch (err) {
      authFlows.deleting = false;
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setDeleteError('Incorrect password.');
      } else {
        setDeleteError(err.message);
      }
      setDeleting(false);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <h1 style={styles.title}>Settings</h1>

        <div style={styles.card}>
          <h2 style={styles.sectionTitle}>Email Domain Restriction</h2>
          <p style={styles.desc}>
            When set, only employees with emails ending in this domain can be added to your company.
            This cannot be changed once saved.
          </p>

          {loading ? (
            <div style={styles.placeholder}>Loading...</div>
          ) : savedDomain ? (
            <div style={styles.lockedRow}>
              <div style={styles.lockedBadge}>
                <span style={styles.lockIcon}>🔒</span>
                <span style={styles.lockedDomain}>{savedDomain}</span>
              </div>
              <span style={styles.lockedNote}>Domain is locked and cannot be changed.</span>
            </div>
          ) : (
            <form onSubmit={handleSave} style={styles.form}>
              {error && <div style={styles.error}>{error}</div>}
              <div style={styles.inputRow}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="@yourcompany.com"
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  required
                />
                <button style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }} type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Domain'}
                </button>
              </div>
              <p style={styles.hint}>Example: @ghadir.com — employees must have this email domain.</p>
            </form>
          )}
        </div>

        <div style={{ ...styles.card, marginTop: 24 }}>
          <h2 style={styles.sectionTitle}>Time-Off Allowances</h2>
          <p style={styles.desc}>
            How many sick and vacation days each employee gets per calendar year.
            Pending and approved requests both count against the allowance.
          </p>

          {loading ? (
            <div style={styles.placeholder}>Loading...</div>
          ) : (
            <form onSubmit={handleSaveLimits} style={styles.form}>
              {limitsMsg && (
                <div style={limitsMsg.ok ? styles.success : styles.error}>{limitsMsg.text}</div>
              )}
              <div style={styles.limitsRow}>
                {Object.keys(TIME_OFF_TYPES).map(key => (
                  <div key={key} style={styles.limitGroup}>
                    <label style={styles.limitLabel}>{TIME_OFF_TYPES[key].label}s / year</label>
                    <input
                      style={styles.limitInput}
                      value={limitInputs[key]}
                      maxLength={3}
                      onChange={e => setLimitInputs({ ...limitInputs, [key]: e.target.value.replace(/[^0-9]/g, '') })}
                    />
                  </div>
                ))}
                <button style={{ ...styles.saveBtn, opacity: limitsSaving ? 0.7 : 1, alignSelf: 'flex-end' }} type="submit" disabled={limitsSaving}>
                  {limitsSaving ? 'Saving...' : 'Save Allowances'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div style={styles.dangerCard}>
          <h2 style={styles.dangerTitle}>Danger Zone</h2>
          <p style={styles.desc}>
            Permanently delete your company account — all employees, shifts and time
            entries are erased. This cannot be undone.
          </p>
          <form onSubmit={handleDeleteAccount} style={styles.form}>
            {deleteError && <div style={styles.error}>{deleteError}</div>}
            <div style={styles.inputRow}>
              <input
                style={styles.input}
                type="password"
                placeholder="Enter your password to confirm"
                value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                style={{ ...styles.deleteBtn, opacity: deleting ? 0.7 : 1 }}
                type="submit"
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#0f0f0f' },
  container: { padding: '32px 40px', maxWidth: 800, margin: '0 auto' },
  title: { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 32, marginTop: 0 },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: 16,
    padding: '28px 32px',
    border: '1px solid #2a2a2a',
  },
  sectionTitle: { color: '#fff', fontSize: 18, fontWeight: 'bold', marginTop: 0, marginBottom: 8 },
  desc: { color: '#999', fontSize: 14, lineHeight: 1.6, marginBottom: 24, marginTop: 0 },
  placeholder: { color: '#666', fontSize: 14 },
  lockedRow: { display: 'flex', flexDirection: 'column', gap: 10 },
  lockedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    backgroundColor: '#2a2a2a', border: '1px solid #333',
    borderRadius: 10, padding: '12px 18px', width: 'fit-content',
  },
  lockIcon: { fontSize: 16 },
  lockedDomain: { color: '#fff', fontSize: 16, fontWeight: 'bold', fontFamily: 'monospace' },
  lockedNote: { color: '#555', fontSize: 13 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  error: {
    backgroundColor: '#2d1414', color: '#f87171',
    padding: 12, borderRadius: 8, fontSize: 14,
  },
  inputRow: { display: 'flex', gap: 12 },
  input: {
    flex: 1,
    backgroundColor: '#2a2a2a', border: '1px solid #333',
    borderRadius: 10, padding: '12px 16px',
    color: '#fff', fontSize: 15, outline: 'none',
  },
  saveBtn: {
    backgroundColor: '#4f46e5', color: '#fff',
    border: 'none', borderRadius: 10,
    padding: '12px 24px', fontSize: 15, fontWeight: 'bold', cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  hint: { color: '#555', fontSize: 13, margin: 0 },
  success: {
    backgroundColor: '#14532d', color: '#4ade80',
    padding: 12, borderRadius: 8, fontSize: 14,
  },
  limitsRow: { display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' },
  limitGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  limitLabel: {
    color: '#999', fontSize: 12,
    textTransform: 'uppercase', letterSpacing: 1,
  },
  limitInput: {
    backgroundColor: '#2a2a2a', border: '1px solid #333',
    borderRadius: 10, padding: '12px 16px',
    color: '#fff', fontSize: 15, outline: 'none',
    width: 80, textAlign: 'center',
  },
  dangerCard: {
    backgroundColor: '#1e1e1e',
    borderRadius: 16,
    padding: '28px 32px',
    border: '1px solid #7f1d1d',
    marginTop: 24,
  },
  dangerTitle: { color: '#f87171', fontSize: 18, fontWeight: 'bold', marginTop: 0, marginBottom: 8 },
  deleteBtn: {
    backgroundColor: '#7f1d1d', color: '#fff',
    border: 'none', borderRadius: 10,
    padding: '12px 24px', fontSize: 15, fontWeight: 'bold', cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};
